import { z } from 'zod';

// src/contracts/auth.ts
var roleSchema = z.enum(["admin", "client"]);
var authUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.email(),
  role: roleSchema
});
var requestMagicLinkSchema = z.object({
  email: z.email()
});
var bookingStatusSchema = z.enum([
  "requested",
  "confirmed",
  "completed",
  "cancelled"
]);
var serviceOfferingSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  durationMinutes: z.number().int().positive(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string(),
  isActive: z.boolean()
});
var upsertServiceOfferingSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  durationMinutes: z.number().int().positive(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string().default("usd"),
  isActive: z.boolean().default(true)
});
var bookingSchema = z.object({
  id: z.uuid(),
  clientProfileId: z.uuid(),
  serviceOfferingId: z.uuid(),
  startAt: z.iso.datetime(),
  endAt: z.iso.datetime(),
  status: bookingStatusSchema,
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var createBookingRequestSchema = z.object({
  serviceOfferingId: z.uuid(),
  startAt: z.iso.datetime(),
  clientName: z.string().min(1),
  clientEmail: z.email(),
  clientPhone: z.string().optional(),
  notes: z.string().optional()
});
var updateBookingStatusSchema = z.object({
  status: bookingStatusSchema
});
var availabilityQuerySchema = z.object({
  serviceOfferingId: z.uuid(),
  from: z.iso.datetime(),
  to: z.iso.datetime()
});
var availabilityRuleSchema = z.object({
  id: z.uuid(),
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string(),
  endTime: z.string(),
  timezone: z.string(),
  isActive: z.boolean()
});
var upsertAvailabilityRuleSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  endTime: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  timezone: z.string().default("UTC"),
  isActive: z.boolean().default(true)
});
var availableSlotSchema = z.object({
  startAt: z.iso.datetime(),
  endAt: z.iso.datetime()
});
var bookingTransitions = {
  requested: ["confirmed", "cancelled"],
  confirmed: ["completed", "cancelled"],
  completed: [],
  cancelled: []
};
function canTransitionBooking(from, to) {
  return from === to || bookingTransitions[from].includes(to);
}
var bookingStatusLabels = {
  requested: "Requested",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled"
};
var adminCreateBookingSchema = z.object({
  clientProfileId: z.uuid(),
  serviceOfferingId: z.uuid(),
  startAt: z.iso.datetime(),
  /** A booking made by hand is usually already agreed. */
  status: z.enum(["requested", "confirmed"]).default("confirmed"),
  notes: z.string().trim().max(2e3).transform((value) => value === "" ? null : value).nullable().optional().transform((value) => value ?? null)
});
var rescheduleBookingSchema = z.object({
  startAt: z.iso.datetime()
});
var clientTypeValues = ["personal", "business"];
var clientTypeSchema = z.enum(clientTypeValues);
var clientTypeLabels = {
  personal: "Personal",
  business: "Business"
};
var clientProfileSchema = z.object({
  id: z.uuid(),
  userId: z.string(),
  phone: z.string().nullable(),
  company: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var updateClientProfileSchema = z.object({
  phone: z.string().optional(),
  company: z.string().optional(),
  notes: z.string().optional()
});
var optionalText = (max) => z.string().trim().max(max).transform((value) => value === "" ? null : value).nullable().optional().transform((value) => value ?? null);
var clientInputSchema = z.object({
  type: clientTypeSchema,
  name: z.string().trim().min(1, "A name is required").max(120),
  email: z.email("Enter a valid email address").max(254),
  phone: optionalText(40),
  company: optionalText(120),
  website: optionalText(200),
  notes: optionalText(4e3),
  billingEmail: z.union([z.email("Enter a valid billing email").max(254), z.literal("")]).nullable().optional().transform((value) => value ? value : null),
  billingAddress: optionalText(400),
  taxId: optionalText(60),
  defaultCurrency: optionalText(3),
  defaultPaymentTerms: optionalText(20)
}).refine((value) => value.type !== "business" || value.company !== null, {
  path: ["company"],
  message: "A business client needs a business name"
});
var clientContactInputSchema = z.object({
  name: z.string().trim().min(1, "A name is required").max(120),
  email: z.union([z.email("Enter a valid email address").max(254), z.literal("")]).nullable().optional().transform((value) => value ? value : null),
  phone: optionalText(40),
  jobTitle: optionalText(80),
  receivesInvoices: z.boolean().default(false)
});
var brandColors = [
  { key: "ink", label: "Ink", hex: "#1e1b2e", light: false },
  { key: "blue", label: "Blue", hex: "#00a6e7", light: true },
  { key: "purple", label: "Purple", hex: "#8a5399", light: false },
  { key: "red", label: "Red", hex: "#d21c5c", light: false },
  { key: "orange", label: "Orange", hex: "#f0971f", light: true },
  { key: "green", label: "Green", hex: "#8bc14a", light: true }
];
var brandColorKeys = brandColors.map((color) => color.key);
var brandColorSchema = z.enum(brandColorKeys);
function brandColorHex(key) {
  return brandColors.find((color) => color.key === key)?.hex ?? brandColors[0].hex;
}
var overlayOpacitySchema = z.number().int().min(0).max(100);
var articleOverlayDefaults = {
  color: "ink",
  opacity: 80
};

// src/contracts/content.ts
var postStatusSchema = z.enum(["draft", "published"]);
var postSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  slug: z.string().min(1),
  excerpt: z.string().nullable(),
  bodyMarkdown: z.string(),
  status: postStatusSchema,
  coverImageUrl: z.url().nullable(),
  /** The tint over the cover picture at the top of the article. */
  overlayColor: brandColorSchema,
  overlayOpacity: overlayOpacitySchema,
  publishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var articleLimits = {
  title: 160,
  slug: 120,
  excerpt: 400,
  body: 1e5
};
var articleFieldsSchema = z.object({
  title: z.string().trim().min(1, "Give the article a title").max(articleLimits.title),
  slug: z.string().trim().min(1, "Give the article an address").max(articleLimits.slug).regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Use lower-case letters, numbers and dashes"
  ),
  excerpt: z.string().max(articleLimits.excerpt).optional(),
  bodyMarkdown: z.string().max(articleLimits.body),
  status: postStatusSchema.optional(),
  coverImageUrl: z.url().nullable().optional(),
  overlayColor: brandColorSchema.optional(),
  overlayOpacity: overlayOpacitySchema.optional()
});
var upsertPostSchema = articleFieldsSchema.extend({
  status: postStatusSchema.default("draft")
});
var patchPostSchema = articleFieldsSchema.partial();
var healthResponseSchema = z.object({
  status: z.literal("ok"),
  service: z.string(),
  version: z.string(),
  uptime: z.number().nonnegative(),
  timestamp: z.iso.datetime()
});
var taxModeSchema = z.enum(["exclusive", "inclusive"]);
var paymentTermsValues = [
  "due_on_receipt",
  "net_7",
  "net_15",
  "net_30",
  "net_60"
];
var paymentTermsSchema = z.enum(paymentTermsValues);
var paymentTermsDays = {
  due_on_receipt: 0,
  net_7: 7,
  net_15: 15,
  net_30: 30,
  net_60: 60
};
var paymentTermsLabels = {
  due_on_receipt: "Due on receipt",
  net_7: "Net 7",
  net_15: "Net 15",
  net_30: "Net 30",
  net_60: "Net 60"
};
var currencyCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a three-letter ISO code such as USD");
var currencySchema = z.object({
  code: currencyCodeSchema,
  name: z.string().trim().min(1).max(60),
  symbol: z.string().trim().min(1).max(8),
  /** Decimal places: 2 for USD and GHS, 0 for JPY. */
  minorUnits: z.number().int().min(0).max(4),
  isActive: z.boolean(),
  isDefault: z.boolean()
});
var createCurrencySchema = currencySchema.extend({
  isActive: z.boolean().default(true),
  isDefault: z.boolean().default(false)
});
var updateCurrencySchema = currencySchema.omit({ code: true }).partial();
var taxRuleSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(40),
  /** Basis points: 1250 is 12.5%. Capped at 100%. */
  rateBps: z.number().int().min(0).max(1e4),
  description: z.string().trim().max(200).nullable(),
  isActive: z.boolean(),
  isDefault: z.boolean()
});
var createTaxRuleSchema = taxRuleSchema.omit({ id: true }).extend({
  description: z.string().trim().max(200).nullish(),
  isActive: z.boolean().default(true),
  isDefault: z.boolean().default(false)
});
var updateTaxRuleSchema = taxRuleSchema.omit({ id: true }).partial();
var billingSettingsSchema = z.object({
  businessName: z.string().trim().max(120),
  address: z.string().trim().max(400),
  email: z.union([z.email(), z.literal("")]),
  phone: z.string().trim().max(40),
  taxId: z.string().trim().max(60),
  /** Absolute URL or `data:` URI. The PDF embeds it, so it must be reachable by the server. */
  logoUrl: z.string().trim().max(500),
  paymentInstructions: z.string().trim().max(1e3),
  defaultNotes: z.string().trim().max(1e3),
  defaultTerms: z.string().trim().max(2e3),
  defaultPaymentTerms: paymentTermsSchema,
  /**
   * System-wide: whether prices are entered without tax (tax added on top) or
   * with it. Copied onto each invoice when its draft is created, so changing
   * this later never alters an existing invoice.
   */
  taxMode: taxModeSchema
});
var billingDefaults = {
  businessName: "",
  address: "",
  email: "",
  phone: "",
  taxId: "",
  logoUrl: "",
  paymentInstructions: "",
  defaultNotes: "",
  defaultTerms: "",
  defaultPaymentTerms: "net_30",
  taxMode: "exclusive"
};
var seedCurrencies = [
  {
    code: "USD",
    name: "US dollar",
    symbol: "$",
    minorUnits: 2,
    isActive: true,
    isDefault: true
  },
  {
    code: "GHS",
    name: "Ghana cedi",
    symbol: "GH\u20B5",
    minorUnits: 2,
    isActive: true,
    isDefault: false
  }
];
var invoicingStatusValues = [
  "draft",
  "issued",
  "partially_paid",
  "paid",
  "void"
];
var invoicingStatusSchema = z.enum(invoicingStatusValues);
var invoicingStatusLabels = {
  draft: "Draft",
  issued: "Issued",
  partially_paid: "Partially paid",
  paid: "Paid",
  void: "Void"
};
var MAX_MINOR_UNITS = 2147483647;
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD");
var discountTypeSchema = z.enum(["none", "percent", "amount"]);
var invoiceItemInputSchema = z.object({
  description: z.string().trim().min(1, "Describe the item").max(500),
  /** Up to three decimals: 1.5 hours. */
  quantity: z.number().min(0).max(1e6).refine(
    (value) => Math.abs(value * 1e3 - Math.round(value * 1e3)) < 1e-6,
    "Use at most three decimal places"
  ),
  unitPriceCents: z.number().int().min(0).max(MAX_MINOR_UNITS),
  /** The line's one tax rule, copied from the system list when it is chosen. */
  taxName: z.string().trim().max(40).nullable(),
  taxRateBps: z.number().int().min(0).max(1e4)
});
var invoiceInputSchema = z.object({
  clientProfileId: z.uuid(),
  bookingId: z.uuid().nullish(),
  currency: currencyCodeSchema,
  issueDate: isoDateSchema,
  paymentTerms: paymentTermsSchema,
  /** Omit to derive it from the terms. */
  dueDate: isoDateSchema.nullish(),
  subject: z.string().trim().max(200).nullish(),
  notes: z.string().trim().max(2e3).nullish(),
  terms: z.string().trim().max(4e3).nullish(),
  discountType: discountTypeSchema,
  /** Basis points when `percent`, minor units when `amount`. */
  discountValue: z.number().int().min(0),
  items: z.array(invoiceItemInputSchema).max(200)
}).refine(
  (value) => value.discountType !== "percent" || value.discountValue <= 1e4,
  {
    message: "A percentage discount cannot exceed 100%",
    path: ["discountValue"]
  }
);
var paymentMethodValues = [
  "cash",
  "bank_transfer",
  "mobile_money",
  "card",
  "cheque",
  "other"
];
var paymentMethodSchema = z.enum(paymentMethodValues);
var paymentMethodLabels = {
  cash: "Cash",
  bank_transfer: "Bank transfer",
  mobile_money: "Mobile money",
  card: "Card",
  cheque: "Cheque",
  other: "Other"
};
var amountSchema = z.number().int().min(1).max(MAX_MINOR_UNITS);
var recordPaymentSchema = z.object({
  amountCents: amountSchema,
  paidOn: isoDateSchema,
  method: paymentMethodSchema,
  reference: z.string().trim().max(100).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /** Issue a receipt for this payment straight away. */
  issueReceipt: z.boolean().default(true)
});
var standaloneReceiptSchema = z.object({
  clientProfileId: z.uuid().nullish(),
  payerName: z.string().trim().max(120).nullish(),
  payerEmail: z.union([z.email(), z.literal("")]).nullish(),
  currency: currencyCodeSchema,
  amountCents: amountSchema,
  paidOn: isoDateSchema,
  method: paymentMethodSchema,
  reference: z.string().trim().max(100).nullish(),
  description: z.string().trim().min(1, "Say what the payment was for").max(500),
  notes: z.string().trim().max(500).nullish()
}).refine(
  (value) => Boolean(value.clientProfileId) || Boolean(value.payerName),
  { message: "Enter who the payment is from", path: ["payerName"] }
);
var sendDocumentSchema = z.object({
  to: z.email().max(254),
  /** Other people to copy, such as the finance contact at a business client. */
  cc: z.array(z.email().max(254)).max(10).default([]),
  subject: z.string().trim().min(1).max(200),
  message: z.string().max(5e3).default("")
});
var text = (max) => z.string().trim().min(1).max(max);
var siteLimits = {
  heroPhrase: 40,
  heroBio: 400,
  aboutEyebrow: 60,
  aboutHeading: 120,
  aboutParagraph: 1500,
  quoteLine: 120,
  faqQuestion: 200,
  faqAnswer: 1e3,
  serviceTitle: 80,
  serviceDescription: 240,
  pillarTitle: 60,
  pillarDescription: 240
};
var siteMaxItems = {
  heroPhrases: 6,
  aboutParagraphs: 4,
  quoteLines: 6,
  faqs: 20,
  services: 12,
  pillars: 6
};
var pillarSlugs = [
  "agriculture",
  "resonance",
  "climate",
  "impact",
  "technology",
  "education"
];
var heroSectionSchema = z.object({
  phrases: z.array(text(siteLimits.heroPhrase)).min(1).max(siteMaxItems.heroPhrases),
  bio: text(siteLimits.heroBio)
});
var aboutSectionSchema = z.object({
  eyebrow: text(siteLimits.aboutEyebrow),
  heading: text(siteLimits.aboutHeading),
  paragraphs: z.array(text(siteLimits.aboutParagraph)).min(1).max(siteMaxItems.aboutParagraphs)
});
var favouriteQuoteSectionSchema = z.object({
  lines: z.array(text(siteLimits.quoteLine)).min(1).max(siteMaxItems.quoteLines)
});
var faqsSectionSchema = z.object({
  items: z.array(
    z.object({
      question: text(siteLimits.faqQuestion),
      answer: text(siteLimits.faqAnswer)
    })
  ).min(1).max(siteMaxItems.faqs)
});
var servicesSectionSchema = z.object({
  items: z.array(
    z.object({
      title: text(siteLimits.serviceTitle),
      description: text(siteLimits.serviceDescription)
    })
  ).min(1).max(siteMaxItems.services)
});
var pillarsSectionSchema = z.object({
  items: z.array(
    z.object({
      slug: z.enum(pillarSlugs),
      title: text(siteLimits.pillarTitle),
      description: text(siteLimits.pillarDescription)
    })
  ).min(1).max(siteMaxItems.pillars)
});
var socialUrl = z.union([z.url(), z.literal("")]);
var socialsSectionSchema = z.object({
  facebook: socialUrl,
  tiktok: socialUrl,
  instagram: socialUrl,
  pinterest: socialUrl,
  youtube: socialUrl,
  linkedin: socialUrl,
  whatsapp: socialUrl
});
var siteSectionSchemas = {
  hero: heroSectionSchema,
  about: aboutSectionSchema,
  favouriteQuote: favouriteQuoteSectionSchema,
  faqs: faqsSectionSchema,
  services: servicesSectionSchema,
  pillars: pillarsSectionSchema,
  socials: socialsSectionSchema
};
var siteSectionKeys = Object.keys(siteSectionSchemas);
function isSiteSectionKey(value) {
  return siteSectionKeys.includes(value);
}
var siteDefaults = {
  hero: {
    phrases: [
      "grow businesses",
      "teach technology",
      "build brands",
      "design systems"
    ],
    bio: "A technologist and startup co-founder building cross-sector infrastructure at the intersection of engineering, brand, and human psychology."
  },
  about: {
    eyebrow: "About Me",
    heading: "Bridging engineering, brand, and impact",
    paragraphs: [
      "I started in graphic design and visual communication, because I was always curious why some things feel trustworthy and others feel hollow. My first real work was with smallholder farmers in Ghana, where I saw that design alone could not fix a broken food system.",
      "That pushed me into software engineering. I now build platforms and systems, design brand identities, and work on agri-tech and climate strategy. Every engagement is judged against the ARCITE framework: technical quality, brand coherence, environmental consequence, and measurable impact."
    ]
  },
  favouriteQuote: {
    lines: ["To whom much is given,", "much more", "is expected."]
  },
  faqs: {
    items: [
      {
        question: "What kind of clients do you work with?",
        answer: "I work with startups, NGOs, social enterprises, and established brands across Africa and globally, primarily those operating at the intersection of technology, agriculture, climate, and impact."
      },
      {
        question: "Do you take on freelance or contract engagements?",
        answer: "Yes. I take on select engagements for brand strategy, product engineering, and advisory. Email iam@henryklevor.com to discuss scope and availability."
      },
      {
        question: "What is the ARCITE Framework?",
        answer: "ARCITE is my operating methodology: Agriculture, Resonance, Climate, Impact, Technology, Education. It is the lens through which I diagnose problems and design solutions that compound value over time."
      },
      {
        question: "How do I access your writing and research?",
        answer: "Articles are published on this site under Articles, and completed work is documented under Projects."
      },
      {
        question: "Are you available for speaking or advisory roles?",
        answer: "Yes, on topics spanning brand architecture, agri-tech, African startup ecosystems, and systems thinking in engineering. Send enquiries to iam@henryklevor.com."
      }
    ]
  },
  services: {
    items: [
      {
        title: "Brand Strategy",
        description: "Positioning, narrative, and brand architecture that compound over time."
      },
      {
        title: "Visual Identity & Design Systems",
        description: "Brand systems and design standards applied consistently across every surface."
      },
      {
        title: "Software & Systems Engineering",
        description: "Full-stack products, CI/CD pipelines, and monorepo architecture built to scale."
      },
      {
        title: "Agri-tech & Climate Strategy",
        description: "Consulting on agricultural technology and climate as core business constraints."
      },
      {
        title: "Impact & NGO Infrastructure",
        description: "Platforms for organisations that need to measure real social returns."
      },
      {
        title: "Capacity Building & Mentorship",
        description: "Learning systems and mentorship frameworks that let teams run without me."
      }
    ]
  },
  pillars: {
    items: [
      {
        slug: "agriculture",
        title: "Agriculture",
        description: "Food systems engineering for smallholder farmers across Sub-Saharan Africa."
      },
      {
        slug: "resonance",
        title: "Resonance",
        description: "Brand strategy as trust architecture \u2014 positioning, narrative, and meaning."
      },
      {
        slug: "climate",
        title: "Climate",
        description: "Climate intelligence and sustainability strategy as a core business constraint."
      },
      {
        slug: "impact",
        title: "Impact",
        description: "Honest accounting for the delta between where things were and where they are."
      },
      {
        slug: "technology",
        title: "Technology",
        description: "Software and systems architecture deployed with precision and purpose."
      },
      {
        slug: "education",
        title: "Education",
        description: "Knowledge transfer systems that make change sustainable beyond the engagement."
      }
    ]
  },
  socials: {
    facebook: "https://facebook.com",
    tiktok: "https://tiktok.com",
    instagram: "https://instagram.com",
    pinterest: "https://pinterest.com",
    youtube: "https://youtube.com",
    linkedin: "https://linkedin.com",
    whatsapp: "https://wa.me/"
  }
};
var integrationCatalog = [
  {
    id: "resend",
    name: "Resend",
    description: "Sends invoices, receipts and sign-in links by email.",
    docsUrl: "https://resend.com/api-keys",
    fields: [
      {
        key: "apiKey",
        label: "API key",
        secret: true,
        required: true,
        placeholder: "re_\u2026"
      },
      {
        key: "fromAddress",
        label: "Send from",
        secret: false,
        required: true,
        placeholder: "Henry Klevor <hello@yourdomain.com>",
        help: "Must be on a domain you have verified in Resend."
      }
    ]
  },
  {
    id: "r2",
    name: "Cloudflare R2",
    description: "Stores the images and files you upload to the media library.",
    docsUrl: "https://developers.cloudflare.com/r2/api/tokens/",
    fields: [
      {
        key: "accountId",
        label: "Account ID",
        secret: false,
        required: true,
        placeholder: "32 character id from the R2 overview page"
      },
      {
        key: "accessKeyId",
        label: "Access key ID",
        secret: true,
        required: true
      },
      {
        key: "secretAccessKey",
        label: "Secret access key",
        secret: true,
        required: true
      },
      {
        key: "bucket",
        label: "Bucket name",
        secret: false,
        required: true,
        placeholder: "henryklevor-media"
      },
      {
        key: "publicUrl",
        label: "Public address",
        secret: false,
        required: true,
        placeholder: "https://files.yourdomain.com",
        help: "Where the bucket is served from (a custom domain or the r2.dev address). Files are linked from here."
      }
    ]
  },
  {
    id: "neon",
    name: "Neon storage",
    description: "Stores the images and files you upload to the media library, in a public bucket on your Neon project. Used before Cloudflare R2 when both are set up.",
    docsUrl: "https://neon.com/docs",
    fields: [
      {
        key: "endpoint",
        label: "Endpoint",
        secret: false,
        required: true,
        placeholder: "https://br-\u2026.storage.c-6.eu-central-1.aws.neon.tech",
        help: "AWS_ENDPOINT_URL_S3 from `neon env pull`. Files are linked from this address."
      },
      {
        key: "region",
        label: "Region",
        secret: false,
        required: true,
        placeholder: "eu-central-1"
      },
      {
        key: "accessKeyId",
        label: "Access key ID",
        secret: true,
        required: true
      },
      {
        key: "secretAccessKey",
        label: "Secret access key",
        secret: true,
        required: true
      },
      {
        key: "bucket",
        label: "Bucket name",
        secret: false,
        required: true,
        placeholder: "henryklevor",
        help: "The bucket must allow public reads, or uploaded images will not show."
      }
    ]
  },
  {
    id: "google",
    name: "Google",
    description: "One sign-in for Calendar, Meet, Drive, Contacts and Tasks. Add your OAuth client, then connect your account.",
    docsUrl: "https://console.cloud.google.com/apis/credentials",
    fields: [
      {
        key: "clientId",
        label: "Client ID",
        secret: false,
        required: true,
        placeholder: "123456789-abc.apps.googleusercontent.com"
      },
      {
        key: "clientSecret",
        label: "Client secret",
        secret: true,
        required: true,
        placeholder: "GOCSPX-\u2026"
      }
    ]
  },
  {
    id: "pandadoc",
    name: "PandaDoc",
    description: "Sends contracts and NDAs for signing and tracks who has signed. One of the signing providers the Docs page can use.",
    docsUrl: "https://developers.pandadoc.com/reference/api-key-authentication-process",
    fields: [
      {
        key: "apiKey",
        label: "API key",
        secret: true,
        required: true,
        help: "Use the sandbox key to try things out. Sandbox documents are watermarked and can only go to your own domain; use a production key for real signing."
      },
      {
        key: "webhookKey",
        label: "Webhook shared key",
        secret: true,
        required: false,
        help: "From the Developer Dashboard when you add the webhook. Needed to receive signing updates as they happen."
      }
    ]
  }
];
var integrationIds = integrationCatalog.map(
  (item) => item.id
);
function isIntegrationId(value) {
  return integrationIds.includes(value);
}
function integrationDef(id) {
  return integrationCatalog.find((item) => item.id === id);
}
var saveIntegrationSchema = z.object({
  values: z.record(z.string(), z.string().trim().max(500)).default({}),
  clear: z.array(z.string()).default([])
});
var mediaLimits = {
  maxBytes: 10 * 1024 * 1024,
  imageMimes: ["image/png", "image/jpeg", "image/webp", "image/gif"],
  documentMimes: ["application/pdf"]
};
var mediaAllowedMimes = [
  ...mediaLimits.imageMimes,
  ...mediaLimits.documentMimes
];
function mediaKind(mime) {
  return mediaLimits.imageMimes.includes(mime) ? "image" : "document";
}
var updateMediaSchema = z.object({
  alt: z.string().trim().max(300).transform((value) => value === "" ? null : value).nullable()
});
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
var projectPillars = [
  "Agriculture",
  "Resonance",
  "Climate",
  "Impact",
  "Technology",
  "Education"
];
var projectLimits = {
  title: 120,
  role: 120,
  year: 20,
  status: 40,
  description: 600,
  sections: 12,
  sectionHeading: 120,
  sectionBody: 5e3
};
var slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function slugify(text2) {
  return text2.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
}
var projectInputSchema = z.object({
  title: z.string().trim().min(1, "A title is required").max(projectLimits.title),
  /** Left empty, it is made from the title. */
  slug: z.string().trim().max(80).regex(slugPattern, "Use lower-case letters, numbers and single dashes").or(z.literal("")).default(""),
  role: z.string().trim().max(projectLimits.role).default(""),
  year: z.string().trim().max(projectLimits.year).default(""),
  status: z.string().trim().max(projectLimits.status).default(""),
  pillars: z.array(z.enum(projectPillars)).max(projectPillars.length).default([]),
  description: z.string().trim().max(projectLimits.description).default(""),
  sections: z.array(
    z.object({
      heading: z.string().trim().min(1, "Give the section a heading").max(projectLimits.sectionHeading),
      body: z.string().trim().min(1, "Write something for the section").max(projectLimits.sectionBody)
    })
  ).max(projectLimits.sections).default([]),
  coverMediaId: z.uuid().nullable().default(null),
  coverUrl: z.string().trim().max(500).nullable().default(null),
  published: z.boolean().default(false)
});
var reorderProjectsSchema = z.object({
  ids: z.array(z.uuid()).min(1)
});
var brandingSlots = [
  {
    key: "logoLight",
    label: "Logo",
    help: "Shown on light backgrounds. A dark logo on a transparent background works best."
  },
  {
    key: "logoDark",
    label: "Logo for dark backgrounds",
    help: "Optional. Without it the logo above is inverted in dark mode."
  },
  {
    key: "favicon",
    label: "Favicon",
    help: "The small icon in the browser tab. Square, at least 64 px."
  },
  {
    key: "loginImage",
    label: "Sign-in page image",
    help: "The large picture beside the sign-in form."
  },
  {
    key: "shareImage",
    label: "Social share image",
    help: "Shown when a link to the website is shared. 1200 by 630 px is ideal."
  }
];
var brandingInputSchema = z.object({
  logoLight: z.uuid().nullable().default(null),
  logoDark: z.uuid().nullable().default(null),
  favicon: z.uuid().nullable().default(null),
  loginImage: z.uuid().nullable().default(null),
  shareImage: z.uuid().nullable().default(null)
});
var linkLimits = {
  destination: 2048,
  title: 120,
  codeMin: 3,
  codeMax: 32,
  /** Length of a code made for you. */
  generatedCode: 6
};
var codeAlphabet = "abcdefghjkmnpqrstuvwxyz23456789";
var reservedCodes = [
  "admin",
  "api",
  "app",
  "dashboard",
  "login",
  "l",
  "new",
  "www"
];
var codePattern = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/;
var webAddress = z.url({ protocol: /^https?$/ });
function cleanDestination(input) {
  const text2 = input.trim();
  if (text2 === "" || text2.length > linkLimits.destination) return null;
  if (!webAddress.safeParse(text2).success) return null;
  if (/^https?:\/\/[^/?#]*@/i.test(text2)) return null;
  return text2;
}
var linkInputSchema = z.object({
  destination: z.string().trim().min(1, "Paste the address to send people to").refine(
    (value) => cleanDestination(value) !== null,
    "Enter a full address starting with https://"
  ).transform((value) => cleanDestination(value)),
  /** Left empty, a short code is made for you. */
  code: z.string().trim().toLowerCase().max(linkLimits.codeMax).refine(
    (value) => value === "" || value.length >= linkLimits.codeMin && codePattern.test(value),
    `Use ${linkLimits.codeMin} to ${linkLimits.codeMax} lower-case letters, numbers, dashes or underscores`
  ).refine(
    (value) => !reservedCodes.includes(value),
    "That word is kept back. Choose another"
  ).default(""),
  title: z.string().trim().max(linkLimits.title).default(""),
  enabled: z.boolean().default(true),
  /** An ISO date-time, or null for a link that never expires. */
  expiresAt: z.iso.datetime({ offset: true }).nullable().default(null)
});
var linkStatsDays = 30;
var linkStatsRanges = [7, 30, 90];
var linkStatsRangeSchema = z.coerce.number().int().refine(
  (value) => linkStatsRanges.includes(value),
  "Choose 7, 30 or 90 days"
).default(linkStatsDays);

// src/contracts/google.ts
var googleServices = [
  {
    id: "calendar",
    label: "Calendar",
    description: "Show busy times when offering slots, and add bookings as events.",
    scope: "https://www.googleapis.com/auth/calendar"
  },
  {
    id: "meet",
    label: "Meet",
    description: "Create a meeting link for a booking.",
    scope: "https://www.googleapis.com/auth/meetings.space.created"
  },
  {
    id: "drive",
    label: "Drive",
    description: "Save signed documents and invoices to a folder. Only files made by this app.",
    scope: "https://www.googleapis.com/auth/drive.file"
  },
  {
    id: "contacts",
    label: "Contacts",
    description: "Import people into Clients and keep them in step.",
    scope: "https://www.googleapis.com/auth/contacts"
  },
  {
    id: "tasks",
    label: "Tasks",
    description: "Turn follow-ups, such as an overdue invoice, into Google Tasks.",
    scope: "https://www.googleapis.com/auth/tasks"
  }
];
var googleServiceIds = googleServices.map(
  (item) => item.id
);
var googleIdentityScopes = ["openid", "email"];
function googleScopesFor(services) {
  return [
    ...googleIdentityScopes,
    ...googleServices.filter((service) => services.includes(service.id)).map((service) => service.scope)
  ];
}
function googleServicesFor(scopes) {
  return googleServices.filter((service) => scopes.includes(service.scope)).map((service) => service.id);
}
var docKinds = ["contract", "nda", "proposal", "other"];
var docKindLabels = {
  contract: "Contract",
  nda: "NDA",
  proposal: "Proposal",
  other: "Other"
};
var docStatuses = [
  "draft",
  "sent",
  "viewed",
  "signed",
  "declined",
  "expired",
  "voided"
];
var docStatusLabels = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  signed: "Signed",
  declined: "Declined",
  expired: "Expired",
  voided: "Cancelled"
};
var finalDocStatuses = [
  "signed",
  "declined",
  "expired",
  "voided"
];
var signerStatuses = [
  "pending",
  "viewed",
  "signed",
  "declined"
];
var docLimits = {
  name: 120,
  description: 300,
  title: 160,
  message: 1e3,
  signers: 10,
  roles: 10,
  maxExpiryDays: 180
};
var roleKeyPattern = /^[a-z][a-z0-9_]{0,23}$/;
var docRoleSchema = z.object({
  key: z.string().trim().toLowerCase().regex(
    roleKeyPattern,
    "Use lower-case letters, numbers and underscores, starting with a letter"
  ),
  label: z.string().trim().min(1, "Name the role").max(40)
});
var defaultDocRoles = [{ key: "client", label: "Client" }];
var tagTypes = [
  "signature",
  "initials",
  "date",
  "text",
  "checkbox"
];
function neutralTagPattern() {
  return new RegExp(
    `\\[\\[\\s*(${tagTypes.join("|")})\\s*:\\s*([a-z][a-z0-9_]{0,23})\\s*(?::\\s*([^\\]]{1,60}?))?\\s*\\]\\]`,
    "gi"
  );
}
function findNeutralTags(text2) {
  const tags = [];
  for (const match of text2.matchAll(neutralTagPattern())) {
    tags.push({
      type: match[1].toLowerCase(),
      role: match[2].toLowerCase(),
      label: match[3]?.trim() || null,
      raw: match[0]
    });
  }
  return tags;
}
var templateSources = ["drive", "provider"];
var templateInputSchema = z.object({
  name: z.string().trim().min(1, "Name the template").max(docLimits.name),
  kind: z.enum(docKinds).default("contract"),
  description: z.string().trim().max(docLimits.description).default(""),
  source: z.enum(templateSources).default("drive"),
  roles: z.array(docRoleSchema).min(1, "Add at least one signer role").max(docLimits.roles).default(defaultDocRoles).refine(
    (roles) => new Set(roles.map((role) => role.key)).size === roles.length,
    "Each role needs its own key"
  ),
  /** For `provider` templates: the template's id at the provider. */
  providerTemplateId: z.string().trim().max(200).nullable().default(null)
});
var sendForSigningSchema = z.object({
  templateId: z.uuid("Choose a template"),
  title: z.string().trim().min(1, "Give the document a title").max(docLimits.title),
  clientProfileId: z.uuid().nullable().default(null),
  signers: z.array(
    z.object({
      role: z.string().trim().toLowerCase().regex(roleKeyPattern),
      name: z.string().trim().min(1, "Enter the signer's name").max(120),
      email: z.email("Enter a valid email address").max(200)
    })
  ).min(1).max(docLimits.signers),
  message: z.string().trim().max(docLimits.message).default(""),
  /** 0 means the link does not expire. */
  expiresInDays: z.number().int().min(0).max(docLimits.maxExpiryDays).default(14)
}).refine(
  (input) => new Set(input.signers.map((signer) => signer.email.toLowerCase())).size === input.signers.length,
  {
    message: "Each signer needs a different email address",
    path: ["signers"]
  }
);
var docsSettingsSchema = z.object({
  provider: z.string().trim().min(1).max(40).optional(),
  /** Documents the plan allows per year; null removes the limit. */
  yearlyLimit: z.number().int().min(1).max(1e6).nullable().optional()
}).refine(
  (input) => input.provider !== void 0 || input.yearlyLimit !== void 0,
  { message: "Nothing to change" }
);
var contactLimits = {
  name: 120,
  line: 120,
  phone: 40,
  address: 200,
  note: 300,
  /** Bigger photos make the card slow to download and some phones refuse them. */
  photoMaxBytes: 300 * 1024
};
var contactPhotoMimes = [
  "image/jpeg",
  "image/png",
  "image/gif"
];
var optionalText2 = (max) => z.string().trim().max(max).default("");
var contactCardInputSchema = z.object({
  fullName: z.string().trim().min(1, "Enter the name for the card").max(contactLimits.name),
  jobTitle: optionalText2(contactLimits.line),
  organization: optionalText2(contactLimits.line),
  phone: z.string().trim().max(contactLimits.phone).regex(/^[0-9+()\-.\s]*$/, "Use digits, spaces and + ( ) - only").default(""),
  email: z.union([z.email().max(200), z.literal("")]).default(""),
  website: z.union([z.url().max(300), z.literal("")]).default(""),
  address: optionalText2(contactLimits.address),
  note: optionalText2(contactLimits.note),
  /** Adds the social links from the Website editor to the card. */
  includeSocials: z.boolean().default(true),
  photoMediaId: z.uuid().nullable().default(null),
  /** Switched off, the address answers "not found" and no card is given out. */
  published: z.boolean().default(false)
}).refine(
  (card) => !card.published || card.phone !== "" || card.email !== "" || card.website !== "",
  {
    message: "Add a phone number, email or website before publishing",
    path: ["published"]
  }
);
var emptyContactCard = {
  fullName: "",
  jobTitle: "",
  organization: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  note: "",
  includeSocials: true,
  photoMediaId: null,
  published: false
};
var testimonialLimits = {
  quote: 1200,
  name: 80,
  line: 120,
  photoUrl: 2048
};
var photoUrl = z.url({ protocol: /^https?$/, error: "Use a full web address" }).max(testimonialLimits.photoUrl);
var testimonialInputSchema = z.object({
  quote: z.string().trim().min(1, "Write what they said").max(testimonialLimits.quote),
  authorName: z.string().trim().min(1, "Add their name").max(testimonialLimits.name),
  authorRole: z.string().trim().max(testimonialLimits.line).default(""),
  company: z.string().trim().max(testimonialLimits.line).default(""),
  photoUrl: photoUrl.nullable().default(null),
  published: z.boolean().default(false)
});
var testimonialOrderSchema = z.object({
  ids: z.array(z.uuid()).max(500)
});
var seoLimits = {
  siteName: 60,
  titleTemplate: 80,
  /** Search results cut a title off at about this many characters. */
  title: 70,
  description: 300,
  keyword: 40,
  keywords: 20,
  handle: 16,
  robotsPath: 200,
  robotsPaths: 30
};
var seoPages = [
  { key: "home", label: "Home", path: "/" },
  { key: "profile", label: "Profile", path: "/profile" },
  { key: "framework", label: "The Framework", path: "/framework" },
  { key: "articles", label: "Articles", path: "/articles" },
  { key: "projects", label: "Projects", path: "/projects" },
  { key: "testimonials", label: "Testimonials", path: "/testimonials" },
  { key: "privacy", label: "Privacy policy", path: "/privacy" }
];
var seoPageKeys = seoPages.map((page) => page.key);
var alwaysHiddenPaths = ["/i/", "/r/", "/contact.vcf"];
var titlePlaceholder = "%s";
function cleanSiteUrl(input) {
  const match = /^(https?:\/\/[a-z0-9]([a-z0-9.-]*[a-z0-9])?(:\d{1,5})?)\/?$/i.exec(
    input.trim()
  );
  if (!match) return null;
  const origin = match[1].toLowerCase();
  return z.url({ protocol: /^https?$/ }).safeParse(origin).success ? origin : null;
}
var seoPageSchema = z.object({
  /** Empty means "use the page's own title". */
  title: z.string().trim().max(seoLimits.title).default(""),
  /** Empty means "use the site description". */
  description: z.string().trim().max(seoLimits.description).default(""),
  /** Keeps the page out of search results and the sitemap. */
  hidden: z.boolean().default(false)
});
var robotsPath = z.string().trim().max(seoLimits.robotsPath).regex(/^\/[^\s#]*$/, "Start with a slash, with no spaces, like /private/");
var seoInputSchema = z.object({
  siteName: z.string().trim().min(1, "Give the site a name").max(seoLimits.siteName),
  /** "%s | Henry Klevor": the page's title replaces "%s". */
  titleTemplate: z.string().trim().max(seoLimits.titleTemplate).refine((value) => value.includes(titlePlaceholder), {
    message: `Include ${titlePlaceholder}, where each page's title goes`
  }),
  description: z.string().trim().max(seoLimits.description).default(""),
  keywords: z.array(z.string().trim().min(1).max(seoLimits.keyword)).max(seoLimits.keywords).default([]),
  /** The address people and crawlers use, such as https://www.henryklevor.com. */
  siteUrl: z.string().trim().max(200).refine((value) => cleanSiteUrl(value) !== null, {
    message: "Use a full web address, like https://www.example.com"
  }).transform((value) => cleanSiteUrl(value)),
  /** The picture shown when a link is shared. */
  shareImageUrl: z.url({ protocol: /^https?$/, error: "Use a full web address" }).max(2048).nullable().default(null),
  /** Without the @. */
  twitterHandle: z.string().trim().transform((value) => value.replace(/^@/, "")).refine((value) => value === "" || /^\w{1,15}$/.test(value), {
    message: "Letters, numbers and underscores only, up to 15"
  }).default(""),
  /** Switched off, the whole site asks search engines to stay away. */
  allowIndexing: z.boolean().default(true),
  /** Extra paths for robots.txt to keep crawlers out of. */
  disallow: z.array(robotsPath).max(seoLimits.robotsPaths).default([]),
  pages: z.partialRecord(z.enum(seoPageKeys), seoPageSchema).default({})
});
var seoDefaults = {
  siteName: "Henry Klevor",
  titleTemplate: "%s | Henry Klevor",
  description: "Henry Klevor \u2014 technologist, brand strategist, and founder operating at the intersection of engineering, brand, and impact.",
  keywords: [],
  siteUrl: "http://localhost:3000",
  shareImageUrl: null,
  twitterHandle: "",
  allowIndexing: true,
  disallow: [],
  pages: {}
};
function applyTitleTemplate(template, title) {
  return template.replace(titlePlaceholder, title);
}
var legalSlugs = ["privacy"];
var legalSlugSchema = z.enum(legalSlugs);
var legalPages = {
  privacy: {
    label: "Privacy policy",
    path: "/privacy",
    defaultTitle: "Privacy Policy"
  }
};
var legalLimits = {
  title: 120,
  body: 6e4
};
function legalPlaceholders(markdown) {
  return [...markdown.matchAll(/\[([^\]\n]{1,120})\](?!\()/g)].map(
    (match) => match[1]
  );
}
var legalInputSchema = z.object({
  title: z.string().trim().min(1, "Give the page a title").max(legalLimits.title),
  bodyMarkdown: z.string().max(legalLimits.body),
  published: z.boolean().default(false)
}).superRefine((value, context) => {
  const open = legalPlaceholders(value.bodyMarkdown);
  if (value.published && open.length > 0) {
    context.addIssue({
      code: "custom",
      path: ["bodyMarkdown"],
      message: `Finish the notes in [square brackets] before publishing: ${open.slice(0, 3).map((note) => `[${note}]`).join(", ")}${open.length > 3 ? ", \u2026" : ""}`
    });
  }
});
var legalStarters = {
  privacy: `This policy explains what personal information is collected when you use this website, why, and what you can ask me to do about it. It is written in plain language on purpose.

## Who I am

I am Henry Klevor. You can reach me about anything in this policy at [contact email].

## Browsing this website

This website does not use cookies, and it does not use advertising or analytics trackers. Like any website, the companies that host it receive technical details of each visit, such as your IP address, the page requested and your browser type. These are kept in their server logs for security and to keep the service running.

Some articles and project pages embed videos from YouTube, always in its privacy-enhanced mode, which is designed not to store information about you unless you play the video. Because the video is loaded from YouTube, your browser still contacts YouTube when such a page is shown, so YouTube receives technical details like your IP address. [Remove this paragraph if no videos are embedded.]

## When you book or contact me

If you request a booking, I collect the details you enter: your name, your email address, your phone number (if you give one), the time you choose and any notes you add. I use them only to arrange and carry out the booking, to send you confirmations and reminders, and to keep a record of our work together.

## Clients, invoices and receipts

If we work together, I keep the details needed to do the work and to issue invoices and receipts, such as your name, organisation, contact details and what was agreed or paid for. Invoices, receipts and documents are shared with you through private links; I record when a link is first opened so I know it reached you.

## Short links and QR codes

Some links and QR codes I share go through a short address so that they keep working if the destination changes. When one is opened I count the visit and note only the website it came from and the type of device (for example phone or computer). I do not store your IP address or any information that identifies you.

## My contact card

If you save my contact card, you receive the details I have chosen to put on it, such as my name, phone number, email address and website.

## Who else handles your information

I use trusted services to run this website, and they process information on my behalf: web hosting, a database provider, file storage, an email delivery service, Google services for calendar and email, and an e-signature provider when a document needs signing. They may only use your information to provide their service to me. [Review this list against the services in use.]

## How long I keep it

I keep personal information for as long as it is needed for the purposes above and to meet legal and accounting requirements. [Add the periods that apply, for example how long invoices are kept.]

## Your choices

You can ask me at any time to tell you what I hold about you, to correct it, or to delete it, unless I need to keep it by law. I handle personal data in line with the data protection law that applies to me. [Name the law and the regulator that apply.]

## Changes to this policy

If I change how I handle personal information, I will update this page, and the date at the top will change.

## Questions

Write to me at [contact email] and I will answer as soon as I can.
`
};

export { MAX_MINOR_UNITS, aboutSectionSchema, adminCreateBookingSchema, alwaysHiddenPaths, applyTitleTemplate, articleFieldsSchema, articleLimits, articleOverlayDefaults, authUserSchema, availabilityQuerySchema, availabilityRuleSchema, availableSlotSchema, billingDefaults, billingSettingsSchema, bookingSchema, bookingStatusLabels, bookingStatusSchema, bookingTransitions, brandColorHex, brandColorKeys, brandColorSchema, brandColors, brandingInputSchema, brandingSlots, canTransitionBooking, cleanDestination, cleanSiteUrl, clientContactInputSchema, clientInputSchema, clientProfileSchema, clientTypeLabels, clientTypeSchema, clientTypeValues, codeAlphabet, codePattern, contactCardInputSchema, contactLimits, contactPhotoMimes, createBookingRequestSchema, createCurrencySchema, createTaxRuleSchema, currencyCodeSchema, currencySchema, defaultDocRoles, discountTypeSchema, docKindLabels, docKinds, docLimits, docRoleSchema, docStatusLabels, docStatuses, docsSettingsSchema, emptyContactCard, faqsSectionSchema, favouriteQuoteSectionSchema, finalDocStatuses, findNeutralTags, formatFileSize, googleIdentityScopes, googleScopesFor, googleServiceIds, googleServices, googleServicesFor, healthResponseSchema, heroSectionSchema, integrationCatalog, integrationDef, integrationIds, invoiceInputSchema, invoiceItemInputSchema, invoicingStatusLabels, invoicingStatusSchema, invoicingStatusValues, isIntegrationId, isSiteSectionKey, isoDateSchema, legalInputSchema, legalLimits, legalPages, legalPlaceholders, legalSlugSchema, legalSlugs, legalStarters, linkInputSchema, linkLimits, linkStatsDays, linkStatsRangeSchema, linkStatsRanges, mediaAllowedMimes, mediaKind, mediaLimits, neutralTagPattern, overlayOpacitySchema, patchPostSchema, paymentMethodLabels, paymentMethodSchema, paymentMethodValues, paymentTermsDays, paymentTermsLabels, paymentTermsSchema, paymentTermsValues, pillarSlugs, pillarsSectionSchema, postSchema, postStatusSchema, projectInputSchema, projectLimits, projectPillars, recordPaymentSchema, reorderProjectsSchema, requestMagicLinkSchema, rescheduleBookingSchema, reservedCodes, roleKeyPattern, roleSchema, saveIntegrationSchema, seedCurrencies, sendDocumentSchema, sendForSigningSchema, seoDefaults, seoInputSchema, seoLimits, seoPageKeys, seoPageSchema, seoPages, serviceOfferingSchema, servicesSectionSchema, signerStatuses, siteDefaults, siteLimits, siteMaxItems, siteSectionKeys, siteSectionSchemas, slugify, socialsSectionSchema, standaloneReceiptSchema, tagTypes, taxModeSchema, taxRuleSchema, templateInputSchema, templateSources, testimonialInputSchema, testimonialLimits, testimonialOrderSchema, titlePlaceholder, updateBookingStatusSchema, updateClientProfileSchema, updateCurrencySchema, updateMediaSchema, updateTaxRuleSchema, upsertAvailabilityRuleSchema, upsertPostSchema, upsertServiceOfferingSchema };
//# sourceMappingURL=chunk-7M5GILXI.js.map
//# sourceMappingURL=chunk-7M5GILXI.js.map